import { AlertCircle, Copy } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/utils';

export interface ApiErrorStateProps extends React.HTMLAttributes<HTMLDivElement> {
  message?: string;
  detail?: string;
  traceId?: string;
  onRetry?: () => void;
  size?: 'sm' | 'default';
}

export function ApiErrorState({
  message = 'An unexpected error occurred.',
  detail,
  traceId,
  onRetry,
  size = 'default',
  className,
  ...props
}: ApiErrorStateProps) {
  const [copied, setCopied] = React.useState(false);

  function handleCopy() {
    if (!traceId) return;
    void navigator.clipboard.writeText(traceId).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center gap-3 rounded-[var(--radius)] border border-destructive/30 bg-destructive/10 text-destructive',
        size === 'sm' ? 'px-4 py-4 text-xs' : 'px-6 py-8 text-sm',
        className,
      )}
      {...props}
    >
      <AlertCircle
        className={cn('shrink-0', size === 'sm' ? 'size-5' : 'size-8')}
        aria-hidden="true"
      />
      <div className="flex flex-col items-center gap-1 text-center">
        <p className="font-semibold">{message}</p>
        {detail && (
          <p className={cn('font-mono opacity-60', size === 'sm' ? 'text-[10px]' : 'text-xs')}>
            {detail}
          </p>
        )}
        {traceId && (
          <button
            type="button"
            onClick={handleCopy}
            title="Copy trace ID"
            className="flex items-center gap-1 rounded px-1.5 py-0.5 font-mono text-xs opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-destructive/50 transition-opacity"
          >
            <Copy className="size-3 shrink-0" aria-hidden="true" />
            {copied ? 'Copied!' : `trace: ${traceId}`}
          </button>
        )}
      </div>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-1 rounded border border-destructive/40 px-3 py-1.5 text-xs font-medium hover:bg-destructive/10 focus:outline-none focus:ring-2 focus:ring-destructive/50 transition-colors"
        >
          Try again
        </button>
      )}
    </div>
  );
}
